<script lang="ts">
	import { cfgToThemeCdnPath } from '@infinitro/config';
	import { Verbiage, t } from '@infinitro/content';
	import {
		RightIcon,
		SearchIcon,
		SupportChatArticleDepositIcon,
		SupportChatArticleVerificationIcon,
		SupportChatArticleWithdrawIcon,
		SupportChatSendIcon,
		VipIcon
	} from '@infinitro/icons';
	import type { FeaturedArticleLeaf } from '../utils/support-chat-article-tree';

	export let featuredLeaves: FeaturedArticleLeaf[] = [];
	export let brandTitle = '';
	export let question = '';
	export let visitorName = '';
	export let inputElement: HTMLInputElement | null = null;
	export let onInputKeydown: (event: KeyboardEvent) => void = () => {};
	export let onSearchArticles: () => void = () => {};
	export let onSubmitQuestion: () => void = () => {};
	export let onSelectArticle: (leaf: FeaturedArticleLeaf) => void = () => {};

	type HomeArticleIcon = 'vip' | 'withdraw' | 'deposit' | 'verification' | 'default';

	function iconForFeaturedLeaf(leaf: FeaturedArticleLeaf): HomeArticleIcon {
		const blob = `${leaf.id} ${leaf.articleSlug}`.toLowerCase();
		if (blob.includes('withdraw') || blob.includes('hedge') || blob.includes('uncredited')) {
			return 'withdraw';
		}
		if (blob.includes('deposit')) return 'deposit';
		if (
			blob.includes('two-step') ||
			blob.includes('two-factor') ||
			blob.includes('2fa') ||
			blob.includes('verification') ||
			blob.includes('two_step')
		) {
			return 'verification';
		}
		if (blob.includes('vip') || blob.includes('welcome-bonus') || blob.includes('bonus')) {
			return 'vip';
		}
		return 'default';
	}
</script>

<section class="screen-home">
	<div class="hero-card">
		<p class="hero-greeting">{$t(Verbiage.hello)}, {visitorName}</p>
		<h2 class="hero-title">{$t(Verbiage.support_chat_how_can_we_help)}</h2>
	</div>

	<img
		src={cfgToThemeCdnPath('/support-chat/illustration.png')}
		alt={$t(Verbiage.illustration_alt, 'alts')}
		class="hero-illustration"
	/>

	<div class="content-stack">
		<div class="question-box">
			<label class="sr-only" for="support-chat-home-question">
				{$t(Verbiage.support_chat_ask_a_question_placeholder)}
			</label>
			<input
				id="support-chat-home-question"
				class="question-input"
				type="text"
				bind:value={question}
				bind:this={inputElement}
				placeholder={$t(Verbiage.support_chat_ask_your_question)}
				on:keydown={onInputKeydown}
			/>

			<button
				class="send-action"
				type="button"
				aria-label={$t(Verbiage.support_chat_ask_your_question)}
				on:click={onSubmitQuestion}
			>
				<span class="send-icon">
					<SupportChatSendIcon size={24} />
				</span>
			</button>
		</div>

		<div class="search-box">
			<button class="search-button" type="button" on:click={onSearchArticles}>
				<span>{$t(Verbiage.support_chat_search_in_articles)}</span>
				<span class="search-icon">
					<SearchIcon />
				</span>
			</button>

			<div class="articles-card">
				{#each featuredLeaves as leaf (leaf.id)}
					<button class="article-row" type="button" on:click={() => onSelectArticle(leaf)}>
						<span class="article-lead-icon" aria-hidden="true">
							{#if iconForFeaturedLeaf(leaf) === 'vip'}
								<VipIcon />
							{:else if iconForFeaturedLeaf(leaf) === 'withdraw'}
								<SupportChatArticleWithdrawIcon />
							{:else if iconForFeaturedLeaf(leaf) === 'deposit'}
								<SupportChatArticleDepositIcon />
							{:else if iconForFeaturedLeaf(leaf) === 'verification'}
								<SupportChatArticleVerificationIcon />
							{:else}
								<span class="article-lead-icon-ph"></span>
							{/if}
						</span>
						<span class="article-title">{leaf.title}</span>
						<span class="article-arrow" aria-hidden="true">
							<RightIcon />
						</span>
					</button>
				{/each}
			</div>
		</div>
	</div>
</section>

<style>
	.screen-home {
		position: relative;
		display: flex;
		height: 100%;
		flex-direction: column;
		justify-content: center;
		gap: 0.75rem;
		padding: 0.75rem;
}

	.hero-card {
		position: relative;
		z-index: 10;
		border-radius: 24px;
		padding-left: 1rem;
		padding-right: 1rem;
		padding-top: 0.5rem;
		padding-bottom: 0.5rem;
		color: var(--color-text-100);
}

	.hero-greeting {
		font-weight: 600;
		letter-spacing: 0.025em;
		color: var(--color-text-100);
		opacity: 0.9;
}

	.hero-title {
		font-size: 32px;
		font-weight: 600;
		line-height: 1.08;
}

	.hero-illustration {
		pointer-events: none;
		position: absolute;
		top: 0px;
		right: 0px;
		z-index: 0;
		height: auto;
		width: 150px;
}

	.brand-badge {
		margin-top: 1.25rem;
		display: inline-flex;
		width: -moz-fit-content;
		width: fit-content;
		align-items: center;
		gap: 0.5rem;
		border-radius: 9999px;
		padding-left: 0.75rem;
		padding-right: 0.75rem;
		padding-top: 0.375rem;
		padding-bottom: 0.375rem;
		font-size: 0.75rem;
		line-height: 1rem;
		font-weight: 500;
		color: var(--color-text-100);
		background-color: rgba(var(--color-text-100-rgb), 0.12);
}

	.brand-badge-dot {
		height: 0.625rem;
		width: 0.625rem;
		border-radius: 9999px;
		background-color: var(--color-primary-100);
}

	.content-stack {
		position: relative;
		z-index: 10;
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
		padding-left: 0px;
		padding-right: 0px;
		padding-top: 0.75rem;
		padding-bottom: 0.75rem;
}

	.question-box,
	.search-button,
	.articles-card {
		border-radius: 12px;
		background-color: var(--color-text-100);
}

	.question-box {
		display: flex;
		align-items: center;
		gap: 0.75rem;
		padding-left: 1rem;
		padding-right: 1rem;
		padding-top: 0.5rem;
		padding-bottom: 0.5rem;
}

	.question-input {
		width: 100%;
		background-color: transparent;
		font-size: 0.875rem;
		line-height: 1.25rem;
		font-weight: 600;
		color: var(--color-text-900);
		outline: 2px solid transparent;
		outline-offset: 2px;
}

	.question-input:-moz-placeholder {
		opacity: 0.6;
}

	.question-input:placeholder-shown {
		opacity: 0.6;
}

	.question-input:not(:-moz-placeholder) {
		opacity: 1;
}

	.question-input:not(:placeholder-shown) {
		opacity: 1;
}

	.question-input::-moz-placeholder {
		color: rgba(var(--color-text-900-rgb), 0.48);
	}

	.question-input::placeholder {
		color: rgba(var(--color-text-900-rgb), 0.48);
	}

	.send-action {
		display: flex;
		height: 2rem;
		width: 2rem;
		align-items: center;
		justify-content: center;
		border-radius: 9999px;
		background-color: transparent;
}

	.send-icon {
		display: flex;
		height: 1rem;
		width: 1rem;
		align-items: center;
		justify-content: center;
}

	.search-box {
		display: flex;
		width: 100%;
		flex-direction: column;
		gap: 0.5rem;
		border-radius: 12px;
		--tw-bg-opacity: 1;
		background-color: rgb(255 255 255 / var(--tw-bg-opacity, 1));
		padding: 0.5rem;
}

	.search-button {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 0.75rem;
		border-style: none;
		--tw-bg-opacity: 1;
		background-color: rgb(226 227 228 / var(--tw-bg-opacity, 1));
		padding-left: 1rem;
		padding-right: 1rem;
		padding-top: 0.75rem;
		padding-bottom: 0.75rem;
		text-align: left;
		font-size: 0.875rem;
		line-height: 1.25rem;
		font-weight: 600;
		color: var(--color-text-900);
}

	.search-icon {
		display: block;
		height: 1rem;
		width: 1rem;
		color: var(--color-text-900);
		filter: brightness(0.6);
}

	.articles-card {
		display: flex;
		flex-direction: column;
		gap: 0.125rem;
		padding: 0.25rem;
}

	.article-row {
		display: flex;
		width: 100%;
		min-width: 0px;
		align-items: center;
		gap: 0.75rem;
		border-radius: 10px;
		padding-left: 0.75rem;
		padding-right: 0.75rem;
		padding-top: 0.625rem;
		padding-bottom: 0.625rem;
		text-align: left;
		transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transition-duration: 150ms;
}

	.article-row:hover {
		--tw-bg-opacity: 1;
		background-color: rgb(226 227 228 / var(--tw-bg-opacity, 1));
}

	.article-lead-icon {
		display: flex;
		height: 1.25rem;
		width: 1.25rem;
		flex-shrink: 0;
		align-items: center;
		justify-content: center;
		color: var(--color-text-300);
}

	.article-lead-icon-ph {
		display: block;
		height: 1.25rem;
		width: 1.25rem;
		flex-shrink: 0;
}

	.article-title {
		min-width: 0px;
		flex: 1 1 0%;
		font-size: 0.875rem;
		line-height: 1.25rem;
		font-weight: 600;
		color: var(--color-text-900);
}

	.article-arrow {
		display: block;
		height: 10px;
		width: 10px;
		flex-shrink: 0;
		filter: invert(0.6);
}

	.article-lead-icon :global(svg),
	.article-lead-icon :global(img),
	.search-icon :global(svg),
	.search-icon :global(img),
	.article-arrow :global(svg),
	.article-arrow :global(img) {
		height: 100%;
		width: 100%;
}

	.search-icon :global(svg) {
		color: inherit;
	}

	.search-icon :global(path),
	.search-icon :global(circle),
	.search-icon :global(rect) {
		fill: currentColor;
	}

	.article-lead-icon :global(svg) {
		color: inherit;
	}

	.article-lead-icon :global(path),
	.article-lead-icon :global(circle),
	.article-lead-icon :global(rect) {
		fill: currentColor;
	}

	.article-lead-icon :global(img) {
		opacity: 0.62;
		filter: grayscale(1);
	}

	.sr-only {
		position: absolute;
		height: 1px;
		width: 1px;
		overflow: hidden;
		white-space: nowrap;
		padding: 0px;
		clip: rect(0, 0, 0, 0);
		clip-path: inset(50%);
		margin: -1px;
		border: 0;
}
</style>
